import { Link } from 'react-router-dom';

import { EstadoDeMonitoreo } from '../../presupuesto/componentes/EstadoDeMonitoreo.jsx';
import { avance, importe } from '../../presupuesto/formato.js';
import {
  CASOS_DE_PRESUPUESTO, anchoDeBarra, casoDePresupuesto, claseDeBarra, lineasQueAvisan,
} from '../logica/agregacion.js';
import {
  ESTADO_DE_TARJETA, leerLineasDelPresupuesto, resolverEstadoDeTarjeta,
} from '../logica/estadosHonestos.js';
import { TarjetaCargando, TarjetaConError } from './EstadoDeTarjeta.jsx';

/*
 * T-2 · PRESUPUESTO DEL MES (RF-212, D-312, RN-605 a RN-607).
 *
 * LISTA SOLO LAS CATEGORÍAS QUE AVISAN —«Cerca del límite» o «Excedido»— en el
 * ORDEN en que el servidor las entrega, y al pie el enlace al presupuesto completo
 * (D-312, RN-606). Sin tope de renglones: si avisan muchas, se listan todas.
 *
 * NADA SE RECALCULA AQUÍ (RN-605, RNF-053). El gastado por categoría llega neto de
 * devoluciones, con las mensualidades de compras a meses ya consumiendo
 * presupuesto, con los cancelados fuera y sin dólares; el estado por categoría
 * —DENTRO / CERCA_DEL_LIMITE al 80 % / EXCEDIDO— lo decide el servidor. El Tablero
 * no deduce ni un porcentaje ni un estado.
 *
 * SOLO MXN, porque el presupuesto es solo MXN (decisión vigente del 5/6). El gasto
 * en dólares del mes ya se ve en el resumen del mes.
 *
 * EL DISTINTIVO DE ESTADO ES LA PIEZA DEL 5/6 (`EstadoDeMonitoreo`), reutilizada
 * sin modificarla: así el estado se ve IGUAL que en la pantalla de Presupuesto y
 * no hay dos presentaciones del mismo dato (RN-600, CA-14-08).
 *
 * RN-607 · LOS DOS ESTADOS VACÍOS SON CASOS DISTINTOS y en los dos la tarjeta se
 * muestra: con presupuesto y todo en rango, lo confirma en una línea; sin
 * presupuesto del mes, invita a definirlo.
 *
 * ===========================================================================
 * INCORPORADA A LA REGLA GENERAL · vuelta del 2026-10-08 (D-323, RF-221)
 * ===========================================================================
 * Esta tarjeta era la única exenta del FIX, y la exención dejaba viva la misma
 * confusión: `casoDePresupuesto` devuelve SIN_PRESUPUESTO cuando no encuentra
 * `lineas`, así que una respuesta que no se pudo leer acababa diciendo «aún no
 * defines presupuesto». Un error disfrazado del vacío (RN-623).
 *
 * LO QUE CAMBIA ES SOLO ESO. El vacío LEGÍTIMO —el mes sin presupuesto definido,
 * que es lo que hoy se ve en octubre— sigue diciendo exactamente lo mismo, con
 * el mismo texto y el mismo acceso a definirlo. Y D-312 sigue intacta: solo las
 * categorías que avisan, en el orden del servidor, con su enlace al presupuesto
 * completo. Aquí no se decide ninguna cifra ni ningún estado de categoría.
 */
export function TarjetaDelPresupuesto({ nombreDelMes, presupuesto }) {
  const TITULO = `Presupuesto de ${nombreDelMes}`;

  const lectura = resolverEstadoDeTarjeta(presupuesto, leerLineasDelPresupuesto);

  if (lectura.estado === ESTADO_DE_TARJETA.ERROR) {
    return (
      <TarjetaConError
        titulo={TITULO}
        error={presupuesto.error}
        mensaje={lectura.ilegible ? `No se pudo cargar tu presupuesto de ${nombreDelMes}.` : null}
        alReintentar={presupuesto.recargar}
        dato="presupuesto-del-mes-con-error"
      />
    );
  }

  if (lectura.estado === ESTADO_DE_TARJETA.CARGANDO) {
    return <TarjetaCargando titulo={TITULO} renglones={2} dato="presupuesto-del-mes-cargando" />;
  }

  /* RN-607(b) · el mes no tiene presupuesto definido: se invita, no se oculta.
     ES EL ÚNICO ESTADO EN QUE ESTE TEXTO PUEDE APARECER (RN-623), y con datos
     legibles `casoDePresupuesto` lo resuelve igual que siempre. */
  if (lectura.estado === ESTADO_DE_TARJETA.VACIO) {
    return (
      <section aria-label={TITULO} data-prueba="presupuesto-del-mes">
        <div className="gtit">{TITULO}</div>
        <div className="card">
          <div className="tvacio">
            <b data-prueba="sin-presupuesto-del-mes">Aún no defines presupuesto para {nombreDelMes}</b>
            Pon un importe por categoría y aquí te avisaremos cuando te acerques al límite.
          </div>
          <Link className="btn ancho" to="/presupuesto" data-prueba="definir-presupuesto">
            Definir presupuesto
          </Link>
        </div>
      </section>
    );
  }

  /* RN-607(a) · hay presupuesto y ninguna categoría avisa: se confirma en una
     línea. El reparto entre los dos casos CON DATOS lo sigue decidiendo la misma
     función de siempre, sobre una respuesta que ya se sabe legible. */
  if (casoDePresupuesto(presupuesto.datos) === CASOS_DE_PRESUPUESTO.TODO_EN_RANGO) {
    return (
      <section aria-label={TITULO} data-prueba="presupuesto-del-mes">
        <div className="gtit">{TITULO}</div>
        <div className="card">
          <div className="fila">
            <span className="crece">
              <b data-prueba="todo-en-rango">Todas tus categorías están en rango</b>
              <small>Ninguna se acerca al límite ni lo pasa en {nombreDelMes}.</small>
            </span>
          </div>
          <div className="tpie">
            <span className="crece">Las que van bien están en el presupuesto completo.</span>
            <Link className="btn sec" to="/presupuesto">Ver presupuesto completo</Link>
          </div>
        </div>
      </section>
    );
  }

  const lineas = lineasQueAvisan(presupuesto.datos);

  return (
    <section aria-label={TITULO} data-prueba="presupuesto-del-mes">
      <div className="gtit">{TITULO}</div>
      <div className="card">
        {lineas.map((linea) => (
          <div
            className="fila"
            key={linea.idPresupuestoDeCategoria}
            data-prueba="categoria-que-avisa"
            data-categoria={linea.idCategoria}
            data-clave-estado={linea.claveEstado}
          >
            <span className="crece">
              <b>{linea.nombreCategoria}</b>
              {/* El par se lee como UN importe con su código al final, sin
                  repetirlo dos veces en el mismo renglón (RNF-113, RN-343). */}
              <small data-prueba="avance-de-categoria">
                {importe(linea.gastado).replace(' MXN', '')} de {importe(linea.importeAsignado)}
                {' · '}{avance(linea.porcentajeAvance)}
              </small>
              <span className="tbarra" aria-hidden="true">
                <i
                  className={claseDeBarra(linea.claveEstado)}
                  style={{ width: `${anchoDeBarra(linea.porcentajeAvance)}%` }}
                />
              </span>
            </span>

            {/* Misma pieza, mismo dato y misma presentación que en Presupuesto. */}
            <EstadoDeMonitoreo
              claveEstado={linea.claveEstado}
              nombreEstado={linea.nombreEstado}
              iconoEstado={linea.iconoEstado}
              claveTokenDeColor={linea.claveTokenDeColor}
              marca="estado-de-categoria-que-avisa"
            />
          </div>
        ))}

        <div className="tpie">
          <span className="crece">
            Aquí solo salen las categorías que te están avisando. Las que van bien están en el
            presupuesto completo.
          </span>
          <Link className="btn sec" to="/presupuesto">Ver presupuesto completo</Link>
        </div>
      </div>
    </section>
  );
}
